import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { setCredentials, unsetCredentials } from './auth/authSlice';
import type { RootState } from './store';
import { ReduxQueryTag } from './tag.data';
import { Auth0Client } from '@auth0/auth0-spa-js';

const auth0CMSClientId = process.env.REACT_APP_AUTH0_CMS_CLIENT_ID;
const auth0Audience = process.env.REACT_APP_CMS_AUTH0_AUDIENCE;

/**
 * Base query function with re-Authentication handling and header preparation.
 * This function serves as an interceptor for API requests.
 *
 * @param args - The fetch arguments for the request.
 * @param api - The API object provided by `createApi`.
 * @param extraOptions - Extra options for the query.
 */
const RESULT_ERROR_STATUS = 401;
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const apiBaseUrl = (api.getState() as RootState).config.configData?.appApiBaseUrl;
  const baseUrl = apiBaseUrl ? apiBaseUrl + '/api-v2' : undefined;

  const baseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders(headers, { getState }) {
      const token = (getState() as RootState).auth.accessToken;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    }
  });

  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === RESULT_ERROR_STATUS) {
    try {
      const state = api.getState() as RootState;
      const { auth0Domain } = state.config.configData || {};

      if (!auth0Domain || !auth0CMSClientId) {
        throw new Error('Auth0 configuration is missing');
      }

      const auth0Client = new Auth0Client({
        domain: auth0Domain,
        clientId: auth0CMSClientId,
        authorizationParams: {
          audience: auth0Audience,
          redirect_uri: window.location.origin
        },
        cacheLocation: 'localstorage',
        useRefreshTokens: true
      });

      const accessToken = await auth0Client.getTokenSilently();

      if (!accessToken) {
        throw new Error('Failed to get a new access token');
      }

      api.dispatch(setCredentials({ accessToken, expires: null }));

      result = await baseQuery(args, api, extraOptions);
    } catch (refreshError) {
      api.dispatch(unsetCredentials());
      window.location.href = '/login';
    }
  }
  return result;
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    ReduxQueryTag.accounts,
    ReduxQueryTag.account,
    ReduxQueryTag.accountCopies,
    ReduxQueryTag.accountCopy,
    ReduxQueryTag.accountService,
    ReduxQueryTag.accountTwillioNumbers,
    ReduxQueryTag.intent,
    ReduxQueryTag.slotType,
    ReduxQueryTag.accountIntegration,
    ReduxQueryTag.notify
  ],
  endpoints: builder => ({})
});
